import { Link } from 'react-router-dom';
import { ShoppingCart, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useState } from 'react';

export default function ProductCard({ product }) {
  const { addToCart } = useCart();
  const { user } = useAuth();
  const [adding, setAdding] = useState(false);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    
    // Permetti aggiunta se stock > 0 O se c'è data disponibilità
    const canAdd = product.stock > 0 || product.availability_date;
    
    if (!canAdd) {
      toast.error('Prodotto non disponibile');
      return;
    }

    try {
      setAdding(true);
      await addToCart(product.id);
      
      if (product.availability_date && product.stock === 0) {
        toast.success('Prodotto prenotato! Sarà disponibile dal ' + 
          new Date(product.availability_date).toLocaleDateString('it-IT'));
      } else {
        toast.success('Prodotto aggiunto al carrello!');
      }
    } catch (error) {
      toast.error(error.message || 'Errore durante l\'aggiunta al carrello');
    } finally {
      setAdding(false);
    }
  };

  const getCategoryColor = () => {
    switch (product.category) {
      case 'petshop':
        return 'bg-red-100 text-red-800';
      case 'giardinaggio':
        return 'bg-green-100 text-green-800';
      case 'varie':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Link to={`/products/${product.id}`} className="product-card block" data-testid={`product-card-${product.id}`}>
      <div className="aspect-square overflow-hidden bg-gray-100">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="h-16 w-16 text-gray-400" />
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="mb-2">
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${getCategoryColor()}`}>
            {product.category === 'petshop' && 'Pet Shop'}
            {product.category === 'giardinaggio' && 'Giardinaggio'}
            {product.category === 'varie' && 'Varie'}
          </span>
        </div>

        <h3 className="font-bold text-lg mb-1 line-clamp-2" data-testid={`product-name-${product.id}`}>
          {product.name}
        </h3>
        <p className="text-gray-600 text-sm mb-3 line-clamp-2">{product.description}</p>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-2xl font-bold text-emerald-600" data-testid={`product-price-${product.id}`}>
              €{product.price.toFixed(2)}
            </p>
            {product.stock > 0 ? (
              <p className="text-xs text-gray-500" data-testid={`product-stock-${product.id}`}>
                {product.stock} disponibili
              </p>
            ) : (
              <div data-testid={`product-stock-${product.id}`}>
                {product.availability_date ? (
                  <p className="text-xs text-blue-600 font-semibold">
                    Disponibile dal {new Date(product.availability_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </p>
                ) : product.available_on_order ? (
                  <p className="text-xs text-amber-600 font-semibold">
                    Disponibile su ordinazione
                  </p>
                ) : (
                  <p className="text-xs text-red-600 font-semibold">
                    Esaurito
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Pulsante Prenota per prodotti con data di disponibilità futura */}
          {product.stock === 0 && product.availability_date ? (
            <Button
              onClick={handleAddToCart}
              disabled={adding}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-4 py-2 text-sm font-semibold"
              data-testid={`preorder-${product.id}`}
            >
              {adding ? '...' : '📅 Prenota'}
            </Button>
          ) : (
            <Button
              onClick={handleAddToCart}
              disabled={product.stock === 0 || adding}
              className="bg-rose-600 hover:bg-rose-700 text-white rounded-full h-10 w-10 p-0"
              data-testid={`add-to-cart-${product.id}`}
            >
              <ShoppingCart className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </Link>
  );
}